import { useSubscription } from '@/hooks/useSubscription';
import { User } from '@/types/user';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiLock, FiStar } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Button } from '../Button';

interface PremiumFeatureProps {
  children: React.ReactNode;
  user: User | null;
  feature?: 'plus' | 'pro';
}

export const PremiumFeature = ({
  children,
  user,
  feature,
}: PremiumFeatureProps) => {
  const { hasSubscription } = useSubscription(user);
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();

  // Block scrolling when premium overlay is shown
  useEffect(() => {
    // Check if user has access to the feature
    const hasPlusAccess =
      feature === 'plus' && (hasSubscription('plus') || hasSubscription('pro'));
    const hasProAccess = feature === 'pro' && hasSubscription('pro');
    const hasAccess = hasPlusAccess || hasProAccess;

    if (!hasAccess) {
      // Save the current scroll position
      const scrollPosition = window.scrollY;
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.width = '100%';

      return () => {
        // Restore scrolling when component unmounts
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        // Restore scroll position
        window.scrollTo(0, scrollPosition);
      };
    }
  }, [feature, hasSubscription]);

  if (
    feature === 'plus' &&
    (hasSubscription('plus') || hasSubscription('pro'))
  ) {
    return <>{children}</>;
  } else if (feature === 'pro' && hasSubscription('pro')) {
    return <>{children}</>;
  }

  return (
    <div className="relative">
      {/* Show the children component with subtle blur */}
      <div className="pointer-events-none opacity-70 blur-[2px]">
        {children}
      </div>

      {/* Premium overlay - positioned at the top */}
      <div className="absolute inset-x-0 top-0 z-50 flex justify-center px-4 pt-4">
        <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-xl">
          <div className="flex justify-center">
            <motion.div
              className="mb-4 flex size-12 items-center justify-center rounded-full bg-linko-light text-white"
              initial={{ scale: 0.8 }}
              animate={{
                scale: [1, 1.1, 1],
                backgroundColor: [
                  'rgb(14,165,233)',
                  'rgb(8,47,73)',
                  'rgb(14,165,233)',
                ],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                repeatType: 'reverse',
              }}
            >
              <FiLock className="size-5" />
            </motion.div>
          </div>
          <h3 className="mb-2 text-center text-xl font-semibold">
            {t('common:subscription-required')}
          </h3>
          <p className="mb-6 text-center text-gray-600">
            {t('common:subscription-required-description')}
          </p>
          <div className="flex justify-center">
            <Button
              onClick={() => {
                navigate(`/${i18n.language}/dashboard/pricing`);
              }}
              className="flex items-center gap-2 bg-linko-dark px-6 py-2 text-white transition hover:bg-linko-light hover:text-linko-dark"
            >
              <FiStar className="size-4" />
              {t('common:view-plans')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
